<template>
    <div class="view-company-container">
        <!-- Initial Page Loading Overlay -->
        <v-overlay :value="initialLoading" z-index="999">
            <div class="d-flex flex-column align-center">
                <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
                <div class="mt-4 text-h6 white--text">Loading Company...</div>
            </div>
        </v-overlay>

        <!-- Header Section -->
        <div class="header-section">
            <v-btn text small color="primary" @click="goBack" class="back-btn">
                <v-icon left small>mdi-arrow-left</v-icon>
                Back to Companies
            </v-btn>

            <div class="company-header">
                <!-- Loading State -->
                <div v-if="loadingCompany" style="width: 100%;">
                    <div class="d-flex align-center mb-2">
                        <v-skeleton-loader type="avatar" width="48" height="48" class="mr-3"></v-skeleton-loader>
                        <div style="flex: 1;">
                            <v-skeleton-loader type="heading" width="300"></v-skeleton-loader>
                        </div>
                    </div>
                    <v-skeleton-loader type="text" width="400" class="mt-2"></v-skeleton-loader>
                </div>

                <!-- Loaded State -->
                <template v-else>
                    <div class="company-info">
                        <div class="d-flex align-center mb-2">
                            <v-icon class="company-icon mr-3" color="primary">mdi-domain</v-icon>
                            <h1 class="company-name">{{ companyName }}</h1>
                            <v-chip small class="status-chip" :class="getCompanyStatusClass(company.status)">
                                {{ company.status || 'ACTIVE' }}
                            </v-chip>
                        </div>
                        <p class="company-description">{{ company.description }}</p>
                    </div>
                    <div class="header-actions">
                        <v-btn v-if="isSuperAdmin" text color="primary" class="text-none mr-2"
                            :to="{ name: 'CompanyDetails', params: { id: companyId } }">
                            <v-icon left small>mdi-domain</v-icon>
                            Company settings
                        </v-btn>
                        <v-btn v-if="canEditCompanyDetails" color="primary" outlined @click="openEditCompanyDialog" class="edit-btn">
                            Edit Company
                        </v-btn>
                    </div>
                </template>
            </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-section">
            <div class="stats-grid">
                <div class="stat-card">
                    <div class="stat-content">
                        <v-icon class="stat-icon" color="#4285F4">mdi-target</v-icon>
                        <div class="stat-label">Active Goals</div>
                        <div v-if="loadingGoals" class="stat-value">
                            <v-skeleton-loader type="text" width="40"></v-skeleton-loader>
                        </div>
                        <div v-else class="stat-value">{{ activeGoalsCount }}</div>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-content">
                        <v-icon class="stat-icon" color="#FF9800">mdi-chart-line</v-icon>
                        <div class="stat-label">Completion</div>
                        <div v-if="loadingGoals" class="stat-value">
                            <v-skeleton-loader type="text" width="40"></v-skeleton-loader>
                        </div>
                        <div v-else class="stat-value">{{ completionPercentage }}%</div>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-content">
                        <v-icon class="stat-icon" color="#673AB7">mdi-account-group</v-icon>
                        <div class="stat-label">Team Size</div>
                        <div v-if="loadingCompanies" class="stat-value">
                            <v-skeleton-loader type="text" width="40"></v-skeleton-loader>
                        </div>
                        <div v-else class="stat-value">{{ teamSize }}</div>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-content">
                        <v-icon class="stat-icon" color="#2196F3">mdi-calendar</v-icon>
                        <div class="stat-label">Next Milestone</div>
                        <div v-if="loadingGoals" class="stat-value" style="font-size: 18px;">
                            <v-skeleton-loader type="text" width="60"></v-skeleton-loader>
                        </div>
                        <div v-else class="stat-value" style="font-size: 18px;">{{ nextMilestone }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tabs -->
        <div class="tabs-section">
            <div class="custom-tabs">
                <div class="tab-item" :class="{ active: activeTab === 0 }" @click="activeTab = 0">Overview</div>
                <div class="tab-item" :class="{ active: activeTab === 1 }" @click="activeTab = 1">Members</div>
                <div class="tab-item" :class="{ active: activeTab === 2 }" @click="activeTab = 2">Resources</div>
                <!-- <div class="tab-item disabled">Discussion</div> -->
            </div>
        </div>

        <!-- Content Section -->
        <div class="content-section">
            <!-- Overview Tab -->
            <div v-show="activeTab === 0" class="content-grid">
                <!-- Goals & Initiatives Section -->
                <div class="goals-section">
                    <div class="section-header">
                        <h2 class="section-title">Goals & Initiatives</h2>
                        <v-btn color="primary" small @click="addGoal" class="add-btn">
                            <v-icon left small>mdi-plus</v-icon>
                            Add Goal
                        </v-btn>
                    </div>

                    <!-- Goals Grid -->
                    <div v-if="loadingGoals" class="goals-grid">
                        <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                        <v-skeleton-loader type="card"></v-skeleton-loader>
                    </div>
                    <div v-else-if="companyGoals.length > 0" class="goals-grid">
                        <div v-for="goal in companyGoals" :key="goal.id" class="goal-card" :class="getGoalCardClass(goal.status)" @click="editGoal(goal)">
                            <div class="goal-header">
                                <h3 class="goal-title">{{ goal.name || 'Customer Portal Redesign' }}</h3>
                                <div class="goal-chips">
                                    <v-chip small class="goal-status-chip" :class="getGoalStatusClass(goal.status)">
                                        {{ (goal.status || 'ACTIVE').toUpperCase() }}
                                    </v-chip>
                                    <v-chip small class="goal-type-chip" v-if="getGoalTypeLabel(goal)">
                                        {{ getGoalTypeLabel(goal) }}
                                    </v-chip>
                                </div>
                            </div>

                            <p class="goal-description">
                                {{ goal.description }}
                            </p>

                            <div class="progress-section">
                                <div class="progress-header">
                                    <span class="progress-label">Progress</span>
                                    <div class="progress-stats">
                                        <span class="progress-percentage">{{ getGoalProgress(goal) }}%</span>
                                        <span class="milestone-count">{{ getGoalMilestoneCount(goal) }}</span>
                                    </div>
                                </div>
                                <div class="progress-bar">
                                    <div class="progress-fill" :style="{ width: getGoalProgress(goal) + '%' }"></div>
                                </div>
                                
                                <!-- Team Progress Breakdown -->
                                <div class="team-progress-section mt-2">
                                    <div 
                                        v-for="team in getGoalTeamProgress(goal)" 
                                        :key="team.name"
                                        class="team-progress-item"
                                        @click.stop="openGoalWithTeamFilter(goal, team.name)"
                                    >
                                        <span class="team-name">{{ team.name }}</span>
                                        <span class="team-stats">{{ team.completed }}/{{ team.total }} ({{ team.percentage }}%)</span>
                                    </div>
                                </div>
                            </div>

                            <div class="goal-footer">
                                <!-- Assigned To -->
                                <div class="goal-info-row">
                                    <v-icon small class="info-icon">mdi-account</v-icon>
                                    <span class="info-label">Assigned To</span>
                                </div>
                                <div class="assignees-chips mb-3">
                                    <v-chip v-for="(assignee, index) in getGoalAssignees(goal)" :key="index"
                                        x-small class="mr-1 assignee-chip">
                                        {{ assignee.initials }}
                                        <span class="assignee-name">{{ assignee.name }}</span>
                                    </v-chip>
                                </div>

                                <!-- Deadline -->
                                <div class="goal-info-row">
                                    <v-icon small class="info-icon">mdi-calendar</v-icon>
                                    <span class="info-label">Deadline</span>
                                </div>
                                <div class="goal-info-value mb-3">
                                    {{ formatGoalDeadline(goal) }}
                                </div>

                                <!-- Next Milestone -->
                                <div class="goal-info-row">
                                    <v-icon small class="info-icon">mdi-target</v-icon>
                                    <span class="info-label">Next Milestone</span>
                                </div>
                                <div class="next-milestone-info">
                                    <div class="next-milestone-name">{{ getNextMilestone(goal).name }}</div>
                                    <div class="next-milestone-date">{{ getNextMilestone(goal).date }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty state for goals -->
                    <div v-else class="empty-state">
                        <v-icon size="64" color="#E0E0E0">mdi-target</v-icon>
                        <h3 class="empty-title">No goals yet</h3>
                        <p class="empty-description">Create your first goal to get started</p>
                        <v-btn color="primary" @click="addGoal" class="empty-action-btn">
                            <v-icon left>mdi-plus</v-icon>
                            Add Goal
                        </v-btn>
                    </div>
                </div>

                <!-- Right Sidebar: Milestones & Bottlenecks -->
                <div class="sidebar-section">
                    <!-- Milestones Section -->
                    <div class="milestones-section">
                        <div class="section-header mb-4">
                            <h2 class="section-title">Milestones ({{ totalMilestonesCount }})</h2>
                            <v-btn color="primary" small outlined @click="addMilestone">
                                <v-icon left small>mdi-plus</v-icon>
                                Add Milestone
                            </v-btn>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loadingMilestones">
                            <v-skeleton-loader type="card" class="mb-3"></v-skeleton-loader>
                        </div>

                        <!-- Milestone Cards -->
                        <div v-else-if="sortedMilestones.length > 0" class="milestones-list-wrapper">
                            <div class="milestones-list">
                                <div v-for="milestone in sortedMilestones" :key="`milestone-${milestone.id}`" class="milestone-card">
                                    <!-- Goal Tag at Top -->
                                    <div v-if="milestone.goal_id" class="milestone-goal-tag-top">
                                        <v-icon small class="mr-1" color="#666">mdi-target</v-icon>
                                        <span>{{ getGoalName(milestone.goal_id) }}</span>
                                    </div>

                                    <div class="milestone-header">
                                        <h3 class="milestone-title">{{ milestone.name }}</h3>
                                        <v-chip small :color="getMilestoneStatusColor(milestone.status_id)" dark>
                                            {{ getMilestoneStatusName(milestone.status_id) }}
                                        </v-chip>
                                    </div>

                                    <p v-if="milestone.description" class="milestone-description">
                                        {{ milestone.description }}
                                    </p>

                                    <div class="milestone-meta">
                                        <div v-if="milestone.due_date" class="milestone-date">
                                            <v-icon small :color="isOverdue(milestone.due_date) ? '#f44336' : '#666'" class="mr-1">mdi-calendar</v-icon>
                                            {{ formatDate(milestone.due_date) }}
                                            <v-icon v-if="isOverdue(milestone.due_date)" small color="#f44336" class="ml-1">mdi-alert-circle</v-icon>
                                        </div>
                                    </div>

                                    <div v-if="milestone.assignees && milestone.assignees.length > 0" class="milestone-assignees">
                                        <div class="assignee-avatars">
                                            <v-tooltip v-for="assignee in milestone.assignees.slice(0, 3)" :key="assignee.id" top>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-avatar size="24" :color="getAvatarColor(getInitials(assignee.user?.name || 'U'))" class="assignee-avatar" v-bind="attrs" v-on="on">
                                                        <span class="white--text text-caption">{{ getInitials(assignee.user?.name || 'U') }}</span>
                                                    </v-avatar>
                                                </template>
                                                <span>{{ assignee.user?.name || 'Unknown' }}</span>
                                            </v-tooltip>
                                            <v-avatar v-if="milestone.assignees.length > 3" size="24" color="grey" class="assignee-avatar">
                                                <span class="white--text text-caption">+{{ milestone.assignees.length - 3 }}</span>
                                            </v-avatar>
                                        </div>
                                    </div>

                                    <div class="milestone-actions">
                                        <v-btn text x-small @click.stop="editMilestone(milestone)" class="action-btn">
                                            <v-icon left small>mdi-pencil</v-icon>
                                            Edit
                                        </v-btn>
                                        <v-btn text x-small @click.stop="deleteMilestone(milestone)" class="action-btn" color="error">
                                            <v-icon left small>mdi-delete</v-icon>
                                            Delete
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-else class="milestones-empty">
                            <v-icon size="48" color="#E0E0E0">mdi-flag-checkered</v-icon>
                            <p class="milestones-empty-text">No milestones yet. Add milestones to track progress.</p>
                        </div>
                    </div>

                    <!-- Bottlenecks Section -->
                    <div v-if="loadingBottlenecks" class="bottlenecks-section">
                        <div class="bottlenecks-header">
                            <v-icon class="bottlenecks-icon">mdi-alert-triangle</v-icon>
                            <span class="bottlenecks-title">Loading Bottlenecks...</span>
                        </div>
                        <div class="bottlenecks-content">
                            <div class="bottleneck-item">
                                <v-skeleton-loader type="list-item-two-line" width="100%"></v-skeleton-loader>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="companyBottlenecks.length > 0" class="bottlenecks-section">
                        <div class="bottlenecks-header">
                            <v-icon class="bottlenecks-icon">mdi-alert-triangle</v-icon>
                            <span class="bottlenecks-title">Active Bottlenecks ({{ companyBottlenecks.length }})</span>
                        </div>
                        <div class="bottlenecks-content">
                            <div v-for="bottleneck in companyBottlenecks" :key="bottleneck.id" class="bottleneck-item">
                                <div class="bottleneck-info">
                                    <div class="bottleneck-name">{{ bottleneck.name }}</div>
                                    <div class="bottleneck-meta">
                                        <v-icon small class="meta-icon">mdi-account</v-icon>
                                        <span class="assignee-text">
                                            {{ bottleneck.assignees && bottleneck.assignees.length > 0 ?
                                                getAssigneeNames(bottleneck.assignees) : 'Unassigned' }}
                                        </span>
                                        <v-icon small class="meta-icon">mdi-calendar</v-icon>
                                        <span class="date-text">{{ formatDate(bottleneck.due_date) }}</span>
                                        <span v-if="bottleneck.due_date && $date().isAfter($date(bottleneck.due_date))" class="overdue-badge">(Overdue)</span>
                                    </div>
                                </div>
                                <div class="bottleneck-status">
                                    <span class="status-badge">{{ getStatusText(bottleneck.status_id) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Members Tab -->
            <div v-show="activeTab === 1" class="members-tab-content">
                <div class="members-actions" style="display:flex; justify-content:flex-end; margin-bottom:12px;">
                    <v-btn v-if="canEditCompany" color="primary" @click="openAddMemberDialog" class="empty-action-btn">
                            <v-icon left>mdi-plus</v-icon>
                            Add Member
                    </v-btn>
                </div>
                <!-- Members Grid -->
                <div v-if="companyTeamMembers.length > 0" class="members-grid">

                    <div v-for="member in companyTeamMembers" :key="member.id" class="member-card">
                        <div class="member-avatar" :style="{ backgroundColor: getAvatarColorForUser(member.id) }">
                            {{ getUserInitials(member) }}
                        </div>
                        <div class="member-info">
                            <h3 class="member-name">{{ member.name || `${member.first_name} ${member.last_name}` }}</h3>
                            <p class="member-role">{{ member.position_name || 'Team Member' }}</p>
                            <div class="member-meta">
                                <span class="meta-item">
                                    <v-icon small color="#666" class="mr-1">mdi-briefcase-outline</v-icon>
                                    {{ member.skills && member.skills.length > 0 ? member.skills[0] : 'Engineering' }}
                                </span>
                            </div>
                        </div>
                        <div class="member-stats">
                            <div class="stat-item">
                                <div class="stat-number">{{ getUserTaskCount(member.id).active }}</div>
                                <div class="stat-label">Active</div>
                            </div>
                            <div class="stat-divider"></div>
                            <div class="stat-item">
                                <div class="stat-number">{{ getUserTaskCount(member.id).pending }}</div>
                                <div class="stat-label">Pending</div>
                            </div>
                        </div>
                <div style="display:flex; align-items:center; gap:8px;">
                            <v-btn icon small @click="viewUserDetails(member)" class="member-visibility">
                                <v-icon color="#999">mdi-eye</v-icon>
                            </v-btn>
                            <v-btn icon small @click.stop="confirmRemoveMember(member)" class="member-remove" v-if="canEditCompany">
                                <v-icon color="#e53935">mdi-account-remove</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <v-icon size="64" color="#E0E0E0">mdi-account-group</v-icon>
                    <h3 class="empty-title">No team members</h3>
                    <p class="empty-description">Add members to start collaborating on this project</p>
                    <v-btn v-if="canEditCompany" color="primary" @click="openAddMemberDialog" class="empty-action-btn">
                        <v-icon left>mdi-plus</v-icon>
                        Add Member
                    </v-btn>
                </div>
            </div>

            <!-- Resources Tab -->
            <div v-show="activeTab === 2" class="resources-tab-content">
                <div class="section-header mb-4">
                    <h2 class="section-title">Project Resources</h2>
                    <v-btn color="primary" small @click="showUploadDialog = true" class="add-btn">
                        <v-icon left small>mdi-plus</v-icon>
                        Add Resource
                    </v-btn>
                </div>

                <!-- Resources Grid -->
                <div v-if="filteredCompanyResources.length > 0" class="resources-grid">
                    <ResourceCard v-for="resource in filteredCompanyResources" :key="resource.id" :resource="resource"
                        @pin-toggle="handlePinToggle" @download="handleDownload" @edit="handleEditResource"
                        @delete="handleDeleteResource" />
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <v-icon size="64" color="#E0E0E0">mdi-folder-open-outline</v-icon>
                    <h3 class="empty-title">No resources yet</h3>
                    <p class="empty-description">Add resources to share with your team</p>
                    <v-btn color="primary" @click="showUploadDialog = true" class="empty-action-btn">
                        <v-icon left>mdi-plus</v-icon>
                        Add Resource
                    </v-btn>
                </div>
            </div>
        </div>

        <!-- Edit Company Dialog -->
        <EditCompanyDialog v-model="editCompanyDialog" :company="company" @company-updated="onCompanyUpdated" />

        <!-- Add Member Dialog -->
        <AddMemberDialog 
            v-model="addMemberDialog" 
            :company-id="companyId" 
            :company-name="companyName"
            :existing-member-ids="existingMemberIds"
            @members-added="onMembersAdded" 
        />

        <!-- Create Goal Dialog -->
        <CreateGoalDialog v-model="createGoalDialog" :companyId="companyId" @goal-created="onGoalCreated" />

        <!-- View Goal Dialog -->
        <ViewGoalDialog 
            v-model="viewGoalDialog" 
            :goal="selectedGoal" 
            :initial-team-filter="selectedTeamFilter"
            @edit-goal="openEditFromView" 
            @goal-deleted="onGoalDeleted" 
            @goal-updated="onGoalUpdated" 
        />

        <!-- Edit Goal Dialog -->
        <EditGoalDialog v-model="editGoalDialog" :goal="selectedGoal" @goal-updated="onGoalUpdated"
            @goal-deleted="onGoalDeleted" />

        <!-- Create Milestone Dialog -->
        <!-- Task Creation Dialog for Milestones -->
        <TaskCreationDialog
            v-model="createMilestoneDialog"
            :users="users()"
            :companies="companies()"
            :goals="companyGoals"
            :default-company-id="companyId"
            :task-type="'milestone'"
            @tasks-created="onMilestoneCreated"
        />

        <!-- Edit Milestone Dialog -->
        <EditMilestoneDialog 
            v-model="editMilestoneDialog" 
            :milestone="selectedMilestone"
            :users="users()"
            @milestone-updated="onMilestoneUpdated"
            @milestone-deleted="onMilestoneDeleted" 
        />

        <!-- User Details Dialog -->
        <UserDetailsDialog v-model="userDetailsDialog" :user="selectedUser" :show-tasks="true" :tasks="userTasks"
            :user-stats="selectedUserStats" :loading-tasks="loadingTasks" :task-error="taskError"
            @close="closeUserDetailsDialog" />

        <!-- Upload Resource Dialog -->
        <UploadDocumentDialog v-model="showUploadDialog" :preset-company-id="companyId"
            @upload-document="handleResourceUpload" />

        <!-- Edit Resource Dialog -->
        <EditResourceDialog v-if="resourceToEdit" v-model="showEditResourceDialog" :resource="resourceToEdit"
            :companies="companies()" @update-resource="handleUpdateResource" />

        <!-- Delete Resource Confirmation Dialog -->
        <v-dialog v-model="showDeleteResourceDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline red white--text">
                    <v-icon left color="white">mdi-delete</v-icon>
                    Delete Resource
                </v-card-title>
                <v-card-text class="pa-6">
                    <p class="text-body-1">Are you sure you want to delete this resource?</p>
                    <p v-if="resourceToDelete" class="text-subtitle-1 font-weight-bold mt-2">
                        {{ resourceToDelete.name }}
                    </p>
                    <p class="text-caption grey--text mt-2">
                        This action cannot be undone.
                    </p>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDeleteResource">
                        Cancel
                    </v-btn>
                    <v-btn color="red" dark @click="confirmDeleteResource">
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Remove Member Confirmation Dialog -->
        <v-dialog v-model="showRemoveMemberDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline red white--text">
                    <v-icon left color="white">mdi-account-remove</v-icon>
                    Remove Member
                </v-card-title>
                <v-card-text class="pa-6">
                    <p class="text-body-1">Are you sure you want to remove this member from the company?</p>
                    <p v-if="memberToRemove" class="text-subtitle-1 font-weight-bold mt-2">
                        {{ memberToRemove.name || `${memberToRemove.first_name} ${memberToRemove.last_name}` }}
                    </p>
                    <p class="text-caption grey--text mt-2">
                        This will revoke their access to this company and all its resources.
                    </p>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelRemoveMember">
                        Cancel
                    </v-btn>
                    <v-btn color="red" dark @click="removeMemberFromCompany(memberToRemove.access.find(access => access.company_id === companyId).id)">
                        <v-icon left>mdi-account-remove</v-icon>
                        Remove Member
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EditCompanyDialog from './dialogs/EditCompanyDialog.vue';
import AddMemberDialog from './dialogs/AddMemberDialog.vue';
import CreateGoalDialog from './dialogs/CreateGoalDialog.vue';
import EditGoalDialog from './dialogs/EditGoalDialog.vue';
import ViewGoalDialog from './dialogs/ViewGoalDialog.vue';
import EditMilestoneDialog from './dialogs/EditMilestoneDialog.vue';
import UserDetailsDialog from '../modules/UserDetailsDialog.vue';
import ResourceCard from '../resources/components/ResourceCard.vue';
import UploadDocumentDialog from '../resources/dialogs/UploadDocumentDialog.vue';
import EditResourceDialog from '../resources/dialogs/EditResourceDialog.vue';
import TaskCreationDialog from '../modules/TaskCreationDialog.vue';

export default {
    name: 'ViewCompanyComponent',
    components: {
        EditCompanyDialog,
        AddMemberDialog,
        CreateGoalDialog,
        EditGoalDialog,
        ViewGoalDialog,
        EditMilestoneDialog,
        UserDetailsDialog,
        ResourceCard,
        UploadDocumentDialog,
        EditResourceDialog,
        TaskCreationDialog
    },
    inject: [
        'users',
        'companies',
        'statuses',
        'goals',
        'refreshGoals',
        'bottlenecks',
        'companyResources',
        'addResource',
        'updateResource',
        'removeResource',
        'refreshCompanies',
        'refreshCompanyResources',
        'refreshUsers'
    ],
    data() {
        return {
            initialLoading: true, // Loading state for initial page load
            activeTab: 0,
            editCompanyDialog: false,
            addMemberDialog: false,
            company: {},
            loadingGoals: true,
            loadingBottlenecks: true,
            loadingCompanies: true,
            loadingCompany: true,
            createGoalDialog: false,
            editGoalDialog: false,
            viewGoalDialog: false,
            createMilestoneDialog: false,
            editMilestoneDialog: false,
            selectedGoal: null,
            selectedTeamFilter: null, // For filtering milestones by team when opening goal
            selectedMilestone: null,
            milestones: [],
            loadingMilestones: true,
            allRolesData: [], // Store roles with IDs for mapping goal.roles_included
            userDetailsDialog: false,
            selectedUser: null,
            userTasks: [],
            loadingTasks: false,
            taskError: null,
            selectedUserStats: {
                total_tasks: 0,
                in_progress_tasks: 0,
                completed_tasks: 0,
                overdue_tasks: 0
            },
            showUploadDialog: false,
            showEditResourceDialog: false,
            resourceToEdit: null,
            showDeleteResourceDialog: false,
            resourceToDelete: null,
            showRemoveMemberDialog: false,
            memberToRemove: null
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user'
        }),
        companyId() {
            return parseInt(this.$route.params.id);
        },
        isCompanyLoaded() {
            return !this.loadingCompany && this.company && this.company.id;
        },
        companyName() {
            return this.company?.name || 'Company Name';
        },
        isSuperAdmin() {
            const u = this.get_user;
            if (!u) return false;
            return u.role_members && u.role_members.length > 0
                ? u.role_members.some(m => m.user_role_id === 1)
                : u.role_id === 1;
        },
        // Edit the company record itself: Super Admin only (not Admins or company managers)
        canEditCompanyDetails() {
            return this.isSuperAdmin || (!!this.get_user && [1, 177, 145].includes(this.get_user.id));
        },
        // Members (add / remove): the above plus the company's own managers
        canEditCompany() {
            // Allow admin, manager, and admin-manager roles, plus specific users
            const allowedRoles = [1, 2, 11];
            const allowedUserIds = [1, 177, 145];
            
            return this.get_user && (
                allowedRoles.includes(this.get_user.role_id) ||
                allowedUserIds.includes(this.get_user.id) ||
                this.isCompanyManager
            );
        },
        isCompanyManager() {
            // Per-company manager flag (company_access.is_manager), set by a Super Admin
            const usersList = this.users();
            if (!this.get_user || !Array.isArray(usersList)) return false;
            const me = usersList.find(u => u.id === this.get_user.id);
            return !!(me && Array.isArray(me.access) &&
                me.access.some(a => a.company_id === this.companyId && a.is_manager));
        },
        companyGoals() {
            const goals = this.goals();
            if (!Array.isArray(goals)) return [];
            return goals.filter(g => g.company_id === this.companyId);
        },
        companyBottlenecks() {
            const bottlenecks = this.bottlenecks();
            if (!Array.isArray(bottlenecks)) return [];
            return bottlenecks.filter(b => b.company_id === this.companyId);
        },
        activeGoalsCount() {
            return this.companyGoals.filter(g => g.status === 'active').length;
        },
        completionPercentage() {
            if (this.companyGoals.length === 0) return 0;
            const completed = this.companyGoals.filter(g => g.status === 'completed').length;
            return Math.round((completed / this.companyGoals.length) * 100);
        },
        teamSize() {
            if (this.company.team_members && Array.isArray(this.company.team_members)) {
                return this.company.team_members.length;
            }
            // Fallback: count unique team members from goals and bottlenecks
            const uniqueMembers = new Set();
            this.companyGoals.forEach(goal => {
                if (goal.team_members && Array.isArray(goal.team_members)) {
                    goal.team_members.forEach(m => uniqueMembers.add(m));
                }
            });
            this.companyBottlenecks.forEach(bottleneck => {
                if (bottleneck.assignees && Array.isArray(bottleneck.assignees)) {
                    bottleneck.assignees.forEach(a => uniqueMembers.add(a.user_id));
                }
            });
            return uniqueMembers.size;
        },
        nextMilestone() {
            const goals = this.companyGoals.filter(g => g.due_date);
            if (goals.length === 0) return 'No due date';

            const sortedGoals = goals.sort((a, b) =>
                this.$date(a.due_date).isAfter(this.$date(b.due_date)) ? 1 : -1
            );
            return this.$date(sortedGoals[0].due_date).format('MMM D');
        },
        companyTeamMembers() {
            const usersList = this.users();
            if (!Array.isArray(usersList)) return [];

            // Filter users who have access to this company
            return usersList.filter(user => {
                if (!user.access || !Array.isArray(user.access)) return false;
                return user.access.some(access => access.company_id === this.companyId);
            });
        },
        filteredCompanyResources() {
            const resourcesList = this.companyResources();
            console.log('All Resources:', resourcesList);
            if (!Array.isArray(resourcesList)) return [];

            // Filter resources that belong to this company
            return resourcesList.filter(resource => resource.company_id === this.companyId);
        },
        existingMemberIds() {
            // Extract user IDs from companyTeamMembers
            return this.companyTeamMembers.map(member => member.id);
        },
        sortedMilestones() {
            // Sort milestones by created_at descending (latest first)
            return [...this.milestones].sort((a, b) => {
                const dateA = this.$date(a.created_at);
                const dateB = this.$date(b.created_at);
                return dateB.isAfter(dateA) ? 1 : -1;
            });
        },
        totalMilestonesCount() {
            return this.milestones.length;
        }
    },
    async mounted() {
        this.loadCompany();
        this.checkDataLoaded();
        await this.getUserRoles(); // Load roles for mapping
        await this.loadMilestones();
        
        // Force re-render after roles are loaded to show team progress
        this.$forceUpdate();
        
        // Hide initial loading overlay after all data is loaded
        this.$nextTick(() => {
            this.initialLoading = false;
        });
    },
    methods: {
        async getUserRoles() {
            try {
                const response = await this.$axios.get('api/users/get_user_roles');
                if (response.data && response.data.response) {
                    this.allRolesData = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching user roles:', error);
            }
        },
        getRoleNameById(roleId) {
            if (!roleId || !this.allRolesData.length) return null;
            const role = this.allRolesData.find(r => r.id === parseInt(roleId));
            return role ? role.name : null;
        },
        checkDataLoaded() {
            // Check if data has been loaded from DefaultLayout
            const goalsList = this.goals();
            const bottlenecksList = this.bottlenecks();
            const companiesList = this.companies();

            // Data is loaded if arrays exist (even if empty)
            this.loadingGoals = !goalsList;
            this.loadingBottlenecks = !bottlenecksList;
            this.loadingCompanies = !companiesList;

            // If still loading, check again after a short delay
            if (this.loadingGoals || this.loadingBottlenecks || this.loadingCompanies) {
                setTimeout(() => this.checkDataLoaded(), 500);
            }
        },
        async loadMilestones() {
            if (!this.companyId) return;
            
            this.loadingMilestones = true;
            try {
                const response = await this.$axios.get(`api/tasks/get_tasks_by_company/${this.companyId}`);
                
                if (response.data && response.data.response) {
                    const allTasks = response.data.data || [];
                    
                    // Filter tasks that have a goal_id (are milestones) and add category field
                    this.milestones = allTasks
                        .filter(task => task.goal_id)
                        .map(task => {
                            // Get role name from task's role relationship or role_id
                            const roleName = task.role?.name || 'Unassigned';
                            
                            return {
                                ...task,
                                category: roleName
                            };
                        });
                }
            } catch (error) {
                console.error('Error loading milestones:', error);
                this.$toast.error('Failed to load milestones');
            } finally {
                this.loadingMilestones = false;
            }
        },
        loadCompany() {
            const companiesList = this.companies();
            if (Array.isArray(companiesList) && companiesList.length > 0) {
                this.company = companiesList.find(c => c.id === this.companyId) || {};
                this.loadingCompany = false;
            } else {
                // Companies not loaded yet, check again
                setTimeout(() => {
                    if (this.loadingCompany) {
                        this.loadCompany();
                    }
                }, 300);
            }
        },
        goBack() {
            this.$router.push({ name: 'Projects' });
        },
        openEditCompanyDialog() {
            this.editCompanyDialog = true;
        },
        openAddMemberDialog() {
            this.addMemberDialog = true;
        },
        onCompanyUpdated() {
            this.loadCompany();
            this.$toast.success('Company updated successfully');
        },
        async onMembersAdded() {
            // Refresh users and companies to reflect the new members
            await this.refreshUsers();
            await this.refreshCompanies();
            this.loadCompany();
        },
        addGoal() {
            this.createGoalDialog = true;
        },
        editGoal(goal) {
            this.selectedGoal = goal;
            this.selectedTeamFilter = null; // Reset team filter
            this.viewGoalDialog = true;
        },
        openGoalWithTeamFilter(goal, teamName) {
            this.selectedGoal = goal;
            this.selectedTeamFilter = teamName;
            this.viewGoalDialog = true;
        },
        openEditFromView(goal) {
            this.selectedGoal = goal;
            this.viewGoalDialog = false;
            this.editGoalDialog = true;
        },
        async onGoalCreated(createdGoal) {
            // Refresh both goals and milestones to get updated data
            await Promise.all([
                this.refreshGoals(),
                this.loadMilestones()
            ]);
            
            // Get the refreshed goal from the goals array with updated milestone data
            if (createdGoal && createdGoal.id) {
                const updatedGoal = this.goals().find(g => g.id === createdGoal.id);
                if (updatedGoal) {
                    this.selectedGoal = updatedGoal;
                    this.viewGoalDialog = true;
                }
            }
        },
        async onGoalUpdated() {
            // Refresh goals to get updated milestone counts and metadata
            await this.refreshGoals();
            // Also reload milestones in case they were affected
            await this.loadMilestones();
            // Force re-render to show updated goal data
            this.$forceUpdate();
        },
        onGoalDeleted() {
            // Goal is already removed via optimistic delete
            this.$forceUpdate();
        },
        async onMilestoneCreated() {
            this.$toast.success('Milestone(s) created successfully');
            this.createMilestoneDialog = false;
            await this.loadMilestones();
        },
        async handleMilestoneUpdate(event) {
            // Reload milestones when quick tasks are created or updated
            if (event.type === 'create' || event.type === 'replace') {
                await this.loadMilestones();
            }
        },
        async onMilestoneUpdated() {
            // Refresh milestones after update
            await this.loadMilestones();
        },
        async onMilestoneDeleted() {
            // Refresh milestones after deletion
            await this.loadMilestones();
        },
        getCompanyStatusColor(status) {
            if (!status) return 'success';
            const statusLower = status.toLowerCase();
            if (statusLower === 'active') return 'success';
            if (statusLower === 'on-hold') return 'warning';
            if (statusLower === 'closed') return 'grey';
            return 'success';
        },
        getCompanyStatusClass(status) {
            if (!status) return 'status-active';
            const statusLower = status.toLowerCase();
            if (statusLower === 'active') return 'status-active';
            if (statusLower === 'on-hold') return 'status-warning';
            if (statusLower === 'closed') return 'status-grey';
            return 'status-active';
        },
        getGoalStatusClass(status) {
            if (!status) return 'goal-status-active';
            const statusLower = status.toLowerCase();
            if (statusLower === 'planning') return 'goal-status-planning';
            if (statusLower === 'active') return 'goal-status-active';
            if (statusLower === 'completed') return 'goal-status-completed';
            if (statusLower === 'on-hold') return 'goal-status-warning';
            return 'goal-status-active';
        },
        getGoalStatusColor(status) {
            if (!status) return 'primary';
            const statusLower = status.toLowerCase();
            if (statusLower === 'active') return 'success';
            if (statusLower === 'completed') return 'primary';
            if (statusLower === 'on-hold') return 'warning';
            return 'primary';
        },
        getGoalCardClass(status) {
            if (!status) return 'goal-card-active';
            const statusLower = status.toLowerCase();
            if (statusLower === 'planning') return 'goal-card-planning';
            if (statusLower === 'active') return 'goal-card-active';
            if (statusLower === 'on-hold') return 'goal-card-onhold';
            if (statusLower === 'completed') return 'goal-card-completed';
            return 'goal-card-active';
        },
        getGoalTypeLabel(goal) {
            if (!goal) return null;
            const val = goal.goal_type || goal.type || goal.goalType || goal.category || null;
            // Treat null/undefined goal type as 'new_website' by default
            const resolved = val || 'new_website';
            const v = String(resolved).toLowerCase();
            if (v === 'new_website') return 'New Website';
            if (v === 'new_business_unit') return 'New Business Unit';
            // Fallback: humanize
            const human = String(resolved);
            return human.charAt(0).toUpperCase() + human.slice(1);
        },
        getGoalProgress(goal) {
            if (!goal || !goal.id) return 0;
            
            // Get all milestones for this goal
            const goalMilestones = this.milestones.filter(m => m.goal_id === goal.id);
            
            if (goalMilestones.length === 0) return 0;
            
            // Count completed milestones (status_id that indicates completion)
            // Assuming completed status is when status has 'complete' or 'done' in the name
            const statusesList = this.statuses();
            let completedCount = 0;
            
            if (Array.isArray(statusesList)) {
                const completedStatusIds = statusesList
                    .filter(s => s.name && (s.name.toLowerCase().includes('complete') || s.name.toLowerCase().includes('done')))
                    .map(s => s.id);
                
                completedCount = goalMilestones.filter(m => completedStatusIds.includes(m.status_id)).length;
            }
            
            // Calculate percentage: (completed / total) * 100
            const progress = Math.round((completedCount / goalMilestones.length) * 100);
            
            return progress;
        },
        getGoalMilestoneCount(goal) {
            if (!goal || !goal.id) return '0/0 Milestones';
            
            const goalMilestones = this.milestones.filter(m => m.goal_id === goal.id);
            const totalCount = goalMilestones.length;
            
            if (totalCount === 0) return '0/0 Milestones';
            
            const statusesList = this.statuses();
            let completedCount = 0;
            
            if (Array.isArray(statusesList)) {
                const completedStatusIds = statusesList
                    .filter(s => s.name && (s.name.toLowerCase().includes('complete') || s.name.toLowerCase().includes('done')))
                    .map(s => s.id);
                
                completedCount = goalMilestones.filter(m => completedStatusIds.includes(m.status_id)).length;
            }
            
            return `${completedCount}/${totalCount} Milestones`;
        },
        getGoalTeamProgress(goal) {
            if (!goal || !goal.id) return [];
            
            // Get all milestones for this goal
            const goalMilestones = this.milestones.filter(m => m.goal_id === goal.id);
            
            if (goalMilestones.length === 0) return [];
            
            // Build the allowed teams list - same logic as ViewGoalDialog's milestoneCategories
            const allowedTeams = [];
            
            // Start with default teams (Marketing and Product Developers)
            if (this.allRolesData.some(r => r.name === 'Marketing')) {
                allowedTeams.push('Marketing');
            }
            if (this.allRolesData.some(r => r.name === 'Product Developers')) {
                allowedTeams.push('Product Developers');
            }
            
            // Add teams from goal.roles_included
            if (goal.roles_included && Array.isArray(goal.roles_included) && goal.roles_included.length > 0 && this.allRolesData.length > 0) {
                const savedRoleNames = goal.roles_included
                    .map(roleId => this.getRoleNameById(roleId))
                    .filter(name => name !== null && !allowedTeams.includes(name));
                
                allowedTeams.push(...savedRoleNames);
            }
            
            // If no allowed teams determined, return empty
            if (allowedTeams.length === 0) return [];
            
            // Get completed status IDs
            const statusesList = this.statuses();
            let completedStatusIds = [];
            if (Array.isArray(statusesList)) {
                completedStatusIds = statusesList
                    .filter(s => s.name && (s.name.toLowerCase().includes('complet') || s.name.toLowerCase().includes('done')))
                    .map(s => s.id);
            }
            
            // Group milestones by role/team - ONLY for allowed teams
            const teamGroups = {};
            
            goalMilestones.forEach(milestone => {
                let teamName = null;
                
                if (milestone.category) {
                    teamName = milestone.category;
                } else if (milestone.role && milestone.role.name) {
                    teamName = milestone.role.name;
                }
                
                // Skip if no team name OR if team is not in the allowed teams
                if (!teamName || !allowedTeams.includes(teamName)) {
                    return;
                }
                
                if (!teamGroups[teamName]) {
                    teamGroups[teamName] = {
                        name: teamName,
                        total: 0,
                        completed: 0
                    };
                }
                
                teamGroups[teamName].total++;
                if (completedStatusIds.includes(milestone.status_id)) {
                    teamGroups[teamName].completed++;
                }
            });
            
            return Object.values(teamGroups).map(team => ({
                ...team,
                percentage: team.total > 0 ? Math.round((team.completed / team.total) * 100) : 0
            })).sort((a, b) => b.total - a.total);
        },
        formatGoalDeadline(goal) {
            if (!goal.deadline && !goal.target_date) return 'No deadline';
            const deadline = goal.deadline || goal.target_date;
            return this.$date(deadline).format('M/D/YYYY');
        },
        getNextMilestone(goal) {
            if (!goal || !goal.id) {
                return { name: 'No milestone', date: '' };
            }
            
            const goalMilestones = this.milestones.filter(m => m.goal_id === goal.id);
            if (goalMilestones.length === 0) {
                return { name: 'No milestone', date: '' };
            }
            
            const statusesList = this.statuses();
            let incompleteMilestones = goalMilestones;
            
            // Filter out completed milestones
            if (Array.isArray(statusesList)) {
                const completedStatusIds = statusesList
                    .filter(s => s.name && (s.name.toLowerCase().includes('complete') || s.name.toLowerCase().includes('done')))
                    .map(s => s.id);
                
                incompleteMilestones = goalMilestones.filter(m => !completedStatusIds.includes(m.status_id));
            }
            
            if (incompleteMilestones.length === 0) {
                return { name: 'All milestones completed', date: '' };
            }
            
            // Sort by due_date and get the earliest one
            const sortedMilestones = incompleteMilestones
                .filter(m => m.due_date) // Only milestones with due dates
                .sort((a, b) => this.$date(a.due_date).isBefore(this.$date(b.due_date)) ? -1 : 1);
            
            if (sortedMilestones.length > 0) {
                const nextMilestone = sortedMilestones[0];
                return {
                    name: nextMilestone.name,
                    date: `Due: ${this.$date(nextMilestone.due_date).format('M/D/YYYY')}`
                };
            }
            
            // If no milestones with due dates, return the first incomplete one
            return {
                name: incompleteMilestones[0].name,
                date: 'No due date'
            };
        },
        getGoalAssignees(goal) {
            if (!goal || !goal.assignees || !Array.isArray(goal.assignees) || goal.assignees.length === 0) {
                return [];
            }
            
            return goal.assignees.slice(0, 3).map(assignee => {
                const user = assignee.user || assignee;
                const name = user.name || 'Unknown';
                const nameParts = name.trim().split(' ');
                let initials = 'U';
                
                if (nameParts.length >= 2) {
                    initials = (nameParts[0][0] + nameParts[1][0]).toUpperCase();
                } else if (name.length > 0) {
                    initials = name.substring(0, 2).toUpperCase();
                }
                
                return {
                    name: name,
                    initials: initials
                };
            });
        },
        formatGoalMilestone(goal) {
            if (!goal.due_date) return 'No milestone';
            return this.$date(goal.due_date).format('MMM D, YYYY');
        },
        getMilestoneStatusColor(statusId) {
            const statusesList = this.statuses();
            if (!Array.isArray(statusesList)) return '#9e9e9e';
            const status = statusesList.find(s => s.id === statusId);
            return status?.color || '#9e9e9e';
        },
        getMilestoneStatusName(statusId) {
            const statusesList = this.statuses();
            if (!Array.isArray(statusesList)) return 'Unknown';
            const status = statusesList.find(s => s.id === statusId);
            return status?.name || 'Unknown';
        },
        getGoalName(goalId) {
            const goal = this.companyGoals.find(g => g.id === goalId);
            return goal?.name || 'Unknown Goal';
        },
        isOverdue(dueDate) {
            if (!dueDate) return false;
            return this.$date().isAfter(this.$date(dueDate));
        },
        getInitials(name) {
            if (!name) return 'U';
            const parts = name.trim().split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },
        formatMilestoneDate(milestone) {
            if (!milestone.due_date && !milestone.start_date) return 'No date set';
            if (milestone.start_date && milestone.due_date) {
                return `${this.$date(milestone.start_date).format('MMM D')} - ${this.$date(milestone.due_date).format('MMM D, YYYY')}`;
            }
            if (milestone.due_date) {
                return `Due ${this.$date(milestone.due_date).format('MMM D, YYYY')}`;
            }
            return this.$date(milestone.start_date).format('MMM D, YYYY');
        },
        getMilestoneAssignee(milestone) {
            if (!milestone.assigned_to) return 'Unassigned';
            const usersList = this.users();
            if (!Array.isArray(usersList)) return 'Unknown';
            const user = usersList.find(u => u.id === milestone.assigned_to);
            if (!user) return 'Unknown';
            return user.name || `${user.first_name} ${user.last_name}`;
        },
        addMilestone() {
            this.createMilestoneDialog = true;
        },
        editMilestone(milestone) {
            this.$router.push({ name: 'Dashboard', query: { task: milestone.id } });
        },
        async deleteMilestone(milestone) {
            if (!confirm(`Are you sure you want to delete "${milestone.name}"?`)) return;
            
            try {
                const response = await this.$axios.delete(`api/tasks/delete_task/${milestone.id}`);
                if (response.data.response) {
                    this.$toast.success('Milestone deleted successfully');
                    await this.loadMilestones();
                }
            } catch (error) {
                console.error('Error deleting milestone:', error);
                this.$toast.error('Failed to delete milestone');
            }
        },
        async updateMilestoneStatus(milestone, newStatus) {
            try {
                // Update milestone status via API
                const response = await this.$axios.put(`/api/milestones/${milestone.id}`, {
                    status: newStatus
                });
                
                if (response.data.success || response.data.response) {
                    milestone.status = newStatus;
                    this.$toast.success(`Milestone status updated to ${newStatus}`);
                    // Refresh goals to get updated milestones
                    await this.refreshGoals();
                }
            } catch (error) {
                console.error('Error updating milestone status:', error);
                this.$toast.error('Failed to update milestone status');
            }
        },
        async toggleMilestoneComplete(milestone) {
            try {
                const isCompleted = milestone.status === 'Completed';
                const newStatus = isCompleted ? 'Pending' : 'Completed';
                const newPercentage = isCompleted ? 0 : 100;

                const response = await this.$axios.put(`api/milestones/${milestone.id}`, {
                    status: newStatus,
                    completion_percentage: newPercentage,
                    is_completed: !isCompleted
                });

                if (response.data.response) {
                    this.$toast.success(`Milestone marked as ${newStatus.toLowerCase()}`);
                    await this.loadMilestones();
                }
            } catch (error) {
                console.error('Error toggling milestone completion:', error);
                this.$toast.error('Failed to update milestone');
            }
        },
        getGoalTeam(goal) {
            // Return full names for team members assigned to this goal
            if (!goal.assignees || !Array.isArray(goal.assignees) || goal.assignees.length === 0) {
                return [];
            }

            // Map assignees to their full names
            return goal.assignees.map(assignee => {
                if (!assignee.user) return null;

                const user = assignee.user;

                // Get user full name
                if (user.name) {
                    return user.name;
                }
                if (user.first_name && user.last_name) {
                    return `${user.first_name} ${user.last_name}`;
                }
                if (user.first_name) {
                    return user.first_name;
                }
                return 'Unknown User';
            }).filter(name => name !== null);
        },
        getAvatarColor(initials) {
            const colors = ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info', 'purple', 'pink'];
            let hash = 0;
            for (let i = 0; i < initials.length; i++) {
                hash = initials.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },
        getAssigneeNames(assignees) {
            if (!assignees || !Array.isArray(assignees) || assignees.length === 0) {
                return 'Unassigned';
            }

            const names = assignees.map(assignee => {
                if (assignee.user && assignee.user.name) {
                    return assignee.user.name;
                }
                return null;
            }).filter(name => name !== null);

            if (names.length === 0) return 'Unassigned';
            if (names.length === 1) return names[0];

            return `${names[0]} +${names.length - 1} others`;
        },
        formatDate(date) {
            if (!date) return 'No date';
            return this.$date(date).format('MMM D, YYYY');
        },
        getStatusText(statusId) {
            const statusList = this.statuses();
            if (!Array.isArray(statusList)) return 'Unknown';
            const status = statusList.find(s => s.id === statusId);
            return status ? status.name : 'Unknown';
        },
        getStatusColor(statusId) {
            const statusList = this.statuses();
            if (!Array.isArray(statusList)) return 'grey';
            const status = statusList.find(s => s.id === statusId);

            if (!status) return 'grey';

            const statusName = status.name.toLowerCase();
            if (statusName.includes('progress')) return 'primary';
            if (statusName.includes('stuck') || statusName.includes('block')) return 'error';
            return 'warning';
        },
        getUserInitials(user) {
            if (!user) return '??';
            if (user.first_name && user.last_name) {
                return (user.first_name[0] + user.last_name[0]).toUpperCase();
            }
            if (user.name) {
                const names = user.name.split(' ');
                if (names.length >= 2) {
                    return (names[0][0] + names[1][0]).toUpperCase();
                }
                return user.name.substring(0, 2).toUpperCase();
            }
            return '??';
        },
        getAvatarColorForUser(userId) {
            const colors = ['#4285F4', '#EA4335', '#FBBC04', '#34A853', '#9C27B0', '#FF6D00', '#00ACC1'];
            return colors[userId % colors.length];
        },
        getUserTaskCount() {
            // This would need actual task data - placeholder for now
            return { active: 0, pending: 0 };
        },
        viewUserDetails(user) {
            this.selectedUser = user;
            this.userDetailsDialog = true;
            // Load tasks for the selected user
            this.loadUserTasks(user.id);
            // Load user statistics
            this.loadUserStats(user.id);
        },
        async loadUserTasks(userId) {
            this.loadingTasks = true;
            this.taskError = null;
            this.userTasks = [];

            try {
                const response = await this.$axios.get(`api/tasks/get_tasks_by_assignee/${userId}`);

                // Check different possible response structures
                if (response.data) {
                    if (Array.isArray(response.data)) {
                        // Direct array response
                        this.userTasks = response.data;
                    } else if (response.data.data && Array.isArray(response.data.data)) {
                        // Nested data array
                        this.userTasks = response.data.data;
                    } else if (response.data.response === true && response.data.data) {
                        // Success response with data
                        this.userTasks = Array.isArray(response.data.data) ? response.data.data : [];
                    } else {
                        // Unknown structure
                        this.taskError = 'Unexpected API response format';
                        this.userTasks = [];
                    }
                } else {
                    this.taskError = 'No data received from API';
                    this.userTasks = [];
                }
            } catch (error) {
                console.error('Error loading user tasks:', error);
                this.taskError = `Failed to load user tasks: ${error.message}`;
                this.userTasks = [];
            } finally {
                this.loadingTasks = false;
            }
        },
        async loadUserStats(userId) {
            try {
                const response = await this.$axios.get(`/api/tasks/get_user_tasks_overview/${userId}`);
                this.selectedUserStats = response.data || {
                    total_tasks: 0,
                    in_progress_tasks: 0,
                    completed_tasks: 0,
                    overdue_tasks: 0
                };
            } catch (error) {
                console.error('Error loading user stats:', error);
                this.selectedUserStats = {
                    total_tasks: 0,
                    in_progress_tasks: 0,
                    completed_tasks: 0,
                    overdue_tasks: 0
                };
            }
        },
        confirmRemoveMember(member) {
            this.memberToRemove = member;
            this.showRemoveMemberDialog = true;
        },
        
        cancelRemoveMember() {
            this.showRemoveMemberDialog = false;
            this.memberToRemove = null;
        },
        
        async removeMemberFromCompany(id) {
            if (!id) {
                this.$toast.error('Invalid access ID');
                return;
            }
            
            console.log('Removing member with access ID:', id);
            try {
                // Call the API to delete company access using the provided ID
                const response = await this.$axios.delete(`/api/companies/delete_company_access/${id}`);

                if (response.data.response || response.data.success) {
                    // Use the provider method to refresh companies
                    await this.refreshCompanies();
                    
                    // Refresh users list to update member data
                    await this.refreshUsers();
                    
                    // Reload the local company data
                    this.loadCompany();
                    
                    const memberName = this.memberToRemove?.name || this.memberToRemove?.first_name || 'Member';
                    this.$toast.success(`${memberName} removed from company`);
                } else {
                    this.$toast.error('Failed to remove member from company');
                }
            } catch (error) {
                console.error('Error removing member:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to remove member');
            } finally {
                this.showRemoveMemberDialog = false;
                this.memberToRemove = null;
            }
        },
        
        closeUserDetailsDialog() {
            this.userDetailsDialog = false;
            this.selectedUser = null;
            this.userTasks = [];
            this.loadingTasks = false;
            this.taskError = null;
            // Reset user statistics
            this.selectedUserStats = {
                total_tasks: 0,
                in_progress_tasks: 0,
                completed_tasks: 0,
                overdue_tasks: 0
            };
        },
        async handleResourceUpload(resourceData) {
            try {
                let response;

                if (resourceData.type === 'file' && resourceData.file) {
                    // Handle file upload with FormData
                    const formData = new FormData();
                    formData.append('file', resourceData.file);
                    formData.append('name', resourceData.name);
                    formData.append('description', resourceData.description);
                    formData.append('category', resourceData.category);
                    formData.append('company_id', this.companyId);
                    formData.append('type', 'file');
                    formData.append('is_internal_resource', 'true');
                    if (resourceData.tags && resourceData.tags.length > 0) {
                        formData.append('tags', JSON.stringify(resourceData.tags));
                    }

                    response = await this.$axios.post('/api/resources', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                } else if (resourceData.type === 'url') {
                    // Handle URL resource with JSON data
                    const payload = {
                        name: resourceData.name,
                        description: resourceData.description,
                        category: resourceData.category,
                        company_id: this.companyId,
                        type: 'url',
                        url: resourceData.url,
                        file_type: resourceData.file_type || 'url',
                        tags: resourceData.tags || [],
                        is_internal_resource: true
                    };

                    response = await this.$axios.post('/api/resources', payload);
                } else {
                    throw new Error('Invalid resource data: missing file or URL');
                }

                if (response.data.success || response.data.response) {
                    const newResource = response.data.data || response.data;
                    // Add to resources list via optimistic update
                    this.addResource(newResource);
                    this.$toast.success(`Resource ${resourceData.type === 'file' ? 'uploaded' : 'added'} successfully`);
                    this.showUploadDialog = false;
                } else {
                    this.$toast.error(`Failed to ${resourceData.type === 'file' ? 'upload' : 'add'} resource`);
                }
            } catch (error) {
                console.error('Error with resource:', error);
                this.$toast.error(error.response?.data?.message || `Failed to ${resourceData.type === 'file' ? 'upload' : 'add'} resource`);
            }
        },
        async handlePinToggle(resourceId) {
            const resource = this.companyResources.find(r => r.id === resourceId);
            if (!resource) return;

            const previousPinned = resource.pinned;

            try {
                // Optimistic update
                this.updateResource({ resourceId, updatedData: { pinned: !previousPinned } });

                const response = await this.$axios.patch(`/api/resources/${resourceId}/pin`, {
                    pinned: !previousPinned
                });

                if (response.data.success) {
                    this.$toast.success('Pin status updated');
                } else {
                    // Revert on error
                    this.updateResource({ resourceId, updatedData: { pinned: previousPinned } });
                    this.$toast.error('Failed to update pin status');
                }
            } catch (error) {
                // Revert on error
                this.updateResource({ resourceId, updatedData: { pinned: previousPinned } });
                console.error('Error toggling pin:', error);
                this.$toast.error('Failed to update pin status');
            }
        },
        async handleDownload(resource) {
            try {
                // For URL resources, open the URL directly
                if (resource.type === 'url' || (resource.url && !resource.file_path)) {
                    window.open(resource.url, '_blank');
                    this.$toast.success(`Opened ${resource.name}`);
                } else if (resource.file_path) {
                    // For file resources, download via API
                    const apiUrl = process.env.VUE_APP_API_URL || '';
                    const downloadUrl = `${apiUrl}/api/resources/${resource.id}/download`;
                    
                    const response = await this.$axios.get(downloadUrl, {
                        responseType: 'blob'
                    });

                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', resource.name);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    
                    this.$toast.success(`Downloaded ${resource.name}`);
                } else {
                    this.$toast.error('No file or URL available for this resource');
                }
            } catch (error) {
                console.error('Error accessing resource:', error);
                this.$toast.error('Failed to access resource');
            }
        },
        handleEditResource(resource) {
            this.resourceToEdit = resource;
            this.showEditResourceDialog = true;
        },
        async handleUpdateResource({ resourceId, updateData }) {
            try {
                // Add is_internal_resource field (boolean true/false)
                const payload = {
                    ...updateData,
                    is_internal_resource: updateData.is_internal_resource === true
                };

                const response = await this.$axios.put(`/api/resources/${resourceId}`, payload);

                if (response.data.success || response.data.response) {
                    const updatedResource = response.data.data || response.data;
                    this.updateResource({ resourceId, updatedData: updatedResource });
                    this.$toast.success('Resource updated successfully');
                    this.showEditResourceDialog = false;
                    this.resourceToEdit = null;
                } else {
                    this.$toast.error('Failed to update resource');
                }
            } catch (error) {
                console.error('Error updating resource:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to update resource');
            }
        },
        handleDeleteResource(resource) {
            this.resourceToDelete = resource;
            this.showDeleteResourceDialog = true;
        },
        cancelDeleteResource() {
            this.showDeleteResourceDialog = false;
            this.resourceToDelete = null;
        },
        async confirmDeleteResource() {
            if (!this.resourceToDelete) return;

            try {
                const response = await this.$axios.delete(`/api/resources/${this.resourceToDelete.id}`);

                if (response.data.success || response.data.response) {
                    this.removeResource(this.resourceToDelete.id);
                    this.$toast.success('Resource deleted successfully');
                } else {
                    this.$toast.error('Failed to delete resource');
                }
            } catch (error) {
                console.error('Error deleting resource:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to delete resource');
            } finally {
                this.showDeleteResourceDialog = false;
                this.resourceToDelete = null;
            }
        }
    }
};
</script>

<style scoped>
.view-company-container {
    padding: 20px 24px;
    background-color: #f8f9fa;
    min-height: 100vh;
}

/* Header Section */
.header-section {
    margin-bottom: 32px;
}

.back-btn {
    margin-bottom: 16px;
    text-transform: none;
    font-weight: 500;
    color: #1976d2;
}

.company-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.company-info .d-flex {
    align-items: center;
    margin-bottom: 8px;
}

.company-icon {
    font-size: 32px !important;
    color: #1976d2;
}

.company-name {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
}

.status-chip {
    margin-left: 16px;
    font-weight: 600;
    font-size: 12px;
    height: 24px;
}

.status-active {
    background-color: #e8f5e8 !important;
    color: #2e7d32 !important;
}

.company-description {
    color: #666;
    font-size: 14px;
    margin: 0;
}

.edit-btn {
    text-transform: none;
    font-weight: 500;
}

/* Stats Section */
.stats-section {
    margin-bottom: 32px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.stat-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.stat-icon {
    font-size: 24px !important;
    margin-bottom: 8px;
}

.stat-label {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 4px;
}

.stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
}

/* Tabs Section */
.tabs-section {
    margin-bottom: 32px;
}

.custom-tabs {
    display: flex;
    background: white;
    border-radius: 12px;
    padding: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.tab-item {
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.tab-item.active {
    background-color: #f3f4f6;
    color: #1a1a1a;
    font-weight: 600;
}

.tab-item.disabled {
    color: #9ca3af;
    cursor: not-allowed;
}

.tab-item:not(.disabled) {
    cursor: pointer;
}

.tab-item:not(.disabled):hover:not(.active) {
    background-color: #e5e7eb;
}

/* Content Section */
.content-section {
    margin-bottom: 32px;
}

.content-grid {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 24px;
}

/* Sidebar Section */
.sidebar-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

/* Goals Section */
.goals-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

/* Goals Grid Layout */
.goals-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
}

.add-btn {
    text-transform: none;
    font-weight: 500;
    height: 32px;
}

.goal-card {
    background: white;
    border: 3px solid #e5e7eb;
    border-left-width: 5px;
    border-radius: 12px;
    padding: 24px;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 280px;
}

/* Goal Card Status Border Colors */
.goal-card-planning {
    border-left-color: #FFC107 !important; /* Yellow */
}

.goal-card-active {
    border-left-color: #4CAF50 !important; /* Green */
}

.goal-card-onhold {
    border-left-color: #F44336 !important; /* Red */
}

.goal-card-completed {
    border-left-color: #2196F3 !important; /* Blue */
}

.goal-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
}

.goal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
}

.goal-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    flex: 1;
    margin-right: 16px;
}

.goal-status-chip {
    font-weight: 600;
    font-size: 11px;
    height: 20px;
    min-width: 60px;
}

.goal-chips {
    display: flex;
    gap: 6px;
    align-items: center;
}

.goal-type-chip {
    background-color: #f1f5f9 !important;
    color: #374151 !important;
    font-weight: 600;
    font-size: 11px;
}

.goal-status-planning {
    background-color: #FFF9C4 !important; /* Light Yellow */
    color: #F57F17 !important; /* Dark Yellow */
}

.goal-status-active {
    background-color: #e8f5e8 !important;
    color: #2e7d32 !important;
}

.goal-status-warning {
    background-color: #FFEBEE !important; /* Light Red */
    color: #C62828 !important; /* Dark Red */
}

.goal-status-completed {
    background-color: #E3F2FD !important; /* Light Blue */
    color: #1565C0 !important; /* Dark Blue */
}

.goal-description {
    color: #6b7280;
    font-size: 14px;
    line-height: 1.6;
    margin: 0 0 20px 0;
}

.progress-section {
    margin-bottom: 20px;
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.progress-label {
    font-size: 12px;
    color: #6b7280;
}

.progress-stats {
    display: flex;
    align-items: center;
    gap: 12px;
}

.progress-percentage {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
}

.milestone-count {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
}

.progress-bar {
    width: 100%;
    height: 8px;
    background-color: #f3f4f6;
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background-color: #3b82f6;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.team-progress-section {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.team-progress-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: #f5f5f5;
    border-radius: 12px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
}

.team-progress-item:hover {
    background: #e3f2fd;
    border-color: #2196F3;
    transform: translateY(-1px);
}

.team-progress-item .team-name {
    font-weight: 500;
    color: #424242;
}

.team-progress-item .team-stats {
    color: #757575;
    font-weight: 400;
}

.goal-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: auto;
}

.goal-info-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.info-icon {
    color: #6b7280;
}

.info-label {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
}

.assignees-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.assignee-chip {
    background-color: #f3f4f6 !important;
    color: #1a1a1a !important;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
}

.assignee-chip .assignee-name {
    margin-left: 2px;
}

.goal-info-value {
    font-size: 13px;
    color: #4b5563;
    margin-left: 26px;
}

.next-milestone-info {
    margin-left: 26px;
}

.next-milestone-name {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a1a;
    margin-bottom: 2px;
}

.next-milestone-date {
    font-size: 12px;
    color: #6b7280;
}

/* Milestones Section */
.milestones-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.milestones-empty {
    text-align: center;
    padding: 32px 16px;
}

.milestones-empty-text {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 48px 24px;
}

.empty-title {
    font-size: 18px;
    font-weight: 600;
    color: #6b7280;
    margin: 16px 0 8px 0;
}

.empty-description {
    color: #9ca3af;
    font-size: 14px;
    margin: 0 0 24px 0;
}

.empty-action-btn {
    text-transform: none;
    font-weight: 500;
}

/* Milestones Section */
.milestones-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.milestones-list-wrapper {
    max-height: 500px;
    overflow-y: auto;
    padding-right: 4px;
}

.milestones-list-wrapper::-webkit-scrollbar {
    width: 6px;
}

.milestones-list-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.milestones-list-wrapper::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}
-top {
    font-size: 11px;
    color: #666;
    background: #e3f2fd;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    border: 1px solid #90caf9;
    margin-bottom: 12px;
    width: fit-content;
    font-weight: 500;
}

.milestone-goal-tag-top span {
    color: #1976d2;
}

.milestone-card {
    background: #fafafa;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;
}

.milestone-card:hover {
    background: #f5f5f5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.milestone-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.milestone-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    flex: 1;
}

.milestone-description {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
    line-height: 1.5;
}

.milestone-meta {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}

.milestone-date {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #666;
    font-weight: 500;
}

.milestone-assignees {
    margin-top: 12px;
    margin-bottom: 8px;
}

.assignee-avatars {
    display: flex;
    gap: -4px;
    align-items: center;
}

.assignee-avatar {
    border: 2px solid white;
    margin-right: -8px;
}

.assignee-avatar:last-child {
    margin-right: 0;
}

.milestone-progress-wrapper {
    margin-top: 12px;
    margin-bottom: 12px;
}

.progress-header-small {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}

.progress-label-small {
    font-size: 12px;
    color: #666;
}

.progress-value-small {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
}

.milestone-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 8px;
}

.milestone-actions .action-btn {
    font-size: 12px;
    text-transform: none;
    color: #666;
    padding: 4px 8px;
}

.milestone-actions .action-btn:hover {
    background: rgba(0, 0, 0, 0.04);
}

.milestones-empty {
    text-align: center;
    padding: 48px 24px;
}

.milestones-empty-text {
    color: #999;
    font-size: 14px;
    margin-top: 12px;
}

/* Bottlenecks Section */
.bottlenecks-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.bottlenecks-header {
    display: flex;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 16px;
}

.bottlenecks-icon {
    color: #f44336;
    margin-right: 8px;
}

.bottlenecks-title {
    font-size: 16px;
    font-weight: 600;
    color: #f44336;
}

.bottlenecks-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.bottleneck-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px;
    background: #fafafa;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
}

.bottleneck-info {
    flex: 1;
}

.bottleneck-name {
    font-weight: 600;
    font-size: 13px;
    color: #1a1a1a;
    margin-bottom: 4px;
}

.bottleneck-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 11px;
    color: #6b7280;
    gap: 8px;
}

.meta-icon {
    margin-right: 2px;
}

.assignee-text,
.date-text {
    display: flex;
    align-items: center;
}

.overdue-badge {
    color: #dc2626;
    font-weight: 500;
}

.bottleneck-status {
    margin-left: 12px;
}

.status-badge {
    background-color: #dbeafe;
    color: #1d4ed8;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
}

/* Members Tab */
.members-tab-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.members-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.members-grid {
    display: grid;
    gap: 16px;
}

.member-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    transition: all 0.2s ease;
    cursor: pointer;
}

.member-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
}

.member-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    font-weight: 600;
    margin-right: 16px;
    flex-shrink: 0;
}

.member-info {
    flex: 1;
}

.member-name {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 4px 0;
}

.member-role {
    font-size: 14px;
    color: #666;
    margin: 0 0 8px 0;
}

.member-meta {
    display: flex;
    align-items: center;
    gap: 16px;
}

.meta-item {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #666;
}

.member-stats {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-right: 24px;
}

.stat-item {
    text-align: center;
}

.stat-number {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
}

.stat-label {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

.stat-divider {
    width: 1px;
    height: 40px;
    background-color: #e5e7eb;
}

.member-visibility {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.member-card:hover .member-visibility {
    opacity: 1;
}

/* Remove button: appears on hover like visibility */
.member-remove {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.member-card:hover .member-remove {
    opacity: 1;
}

/* Resources Tab */
.resources-tab-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
}

.resources-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .content-grid {
        grid-template-columns: 1fr;
        gap: 24px;
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .goals-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .goals-grid {
        grid-template-columns: 1fr;
    }

    .company-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    .custom-tabs {
        flex-direction: column;
    }
}

/* Milestone Tabs Styling */
.milestone-tabs {
    border-bottom: 1px solid #e0e0e0;
}

.milestone-category-section {
    padding: 16px 0;
}
</style>
